import { getSurveyApiBaseUrl } from "./apiOrigin";

const imageBlobUrlCache = new Map<string, string>();
const pendingImageBlobUrlCache = new Map<string, Promise<string>>();
let hasRegisteredBlobCleanup = false;

const isRemoteImageUrl = (value: unknown): value is string =>
  typeof value === "string" && /^https?:\/\//i.test(value);

/**
 * Validates untrusted image URLs. It ensures they point to trusted origins
 * and re-constructs them to strip any hidden credentials.
 */
const buildTrustedImageRequestUrl = (imageUrl: string, apiOrigin: URL): URL => {
  // Parse the input URL relative to current location
  const inputUrl = new URL(imageUrl, window.location.origin);
  const surveyApiOrigin = apiOrigin.origin;
  /* v8 ignore next 6 */
  if (
    inputUrl.origin !== window.location.origin &&
    inputUrl.origin !== surveyApiOrigin
  ) {
    throw new Error("Image request target is invalid");
  }

  // SECURE: Re-construct a completely new URL using only the path and search.
  // This effectively ignores any username, password, or port manipulation in the input string.
  const sanitizedUrl = new URL(inputUrl.origin);
  sanitizedUrl.pathname = inputUrl.pathname;
  sanitizedUrl.search = inputUrl.search;

  return sanitizedUrl;
};

const revokeCachedBlobUrls = () => {
  imageBlobUrlCache.forEach((blobUrl) => {
    URL.revokeObjectURL(blobUrl);
  });
  imageBlobUrlCache.clear();
  pendingImageBlobUrlCache.clear();
};

export const clearImageBlobUrlCache = () => {
  revokeCachedBlobUrls();
};

const registerBlobCleanup = () => {
  if (hasRegisteredBlobCleanup || typeof window === "undefined") return;
  window.addEventListener("beforeunload", revokeCachedBlobUrls);
  hasRegisteredBlobCleanup = true;
};

const getBlobUrlForImage = async (
  imageUrl: string,
  apiOrigin: URL,
): Promise<string> => {
  const cachedBlobUrl = imageBlobUrlCache.get(imageUrl);
  if (cachedBlobUrl) return cachedBlobUrl;

  const pendingBlobUrl = pendingImageBlobUrlCache.get(imageUrl);
  if (pendingBlobUrl) return pendingBlobUrl;

  const trustedImageUrl = buildTrustedImageRequestUrl(imageUrl, apiOrigin);

  // SECURE: Only send credentials (cookies) to the survey API.
  // For same-origin images, use "same-origin". For others, use "omit".
  const surveyApiOrigin = apiOrigin.origin;
  let credentials: RequestCredentials = "omit";

  if (trustedImageUrl.origin === surveyApiOrigin) {
    credentials = "include";
    /* v8 ignore start */
  } else if (trustedImageUrl.origin === window.location.origin) {
    credentials = "same-origin";
  }
  /* v8 ignore end */

  // buildTrustedImageRequestUrl restricts this browser request to trusted origins.
  // nosemgrep
  const blobUrlRequest = fetch(trustedImageUrl.toString(), { credentials })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`Image prefetch failed with ${response.status}`);
      }

      // Safety check: ensure we are actually getting an image
      const contentType = response.headers.get("Content-Type");
      /* v8 ignore next 3 */
      if (contentType && !contentType.startsWith("image/")) {
        throw new Error("Resource is not a valid image");
      }

      const imageBlob = await response.blob();
      const blobUrl = URL.createObjectURL(imageBlob);
      imageBlobUrlCache.set(imageUrl, blobUrl);
      registerBlobCleanup();
      return blobUrl;
    })
    .finally(() => {
      pendingImageBlobUrlCache.delete(imageUrl);
    });

  pendingImageBlobUrlCache.set(imageUrl, blobUrlRequest);
  return blobUrlRequest;
};

const collectRemoteImageUrls = (value: unknown, urls: Set<string>) => {
  if (Array.isArray(value)) {
    value.forEach((item) => collectRemoteImageUrls(item, urls));
    return;
  }
  if (!value || typeof value !== "object") return;

  Object.entries(value as Record<string, unknown>).forEach(([key, val]) => {
    if (key === "image" && isRemoteImageUrl(val)) {
      urls.add(val);
      return;
    }
    collectRemoteImageUrls(val, urls);
  });
};

const prefetchRemoteImagesInBatches = async (
  value: unknown,
  apiOrigin?: URL,
) => {
  const imageUrls = new Set<string>();
  collectRemoteImageUrls(value, imageUrls);

  const normalizedImageUrls = Array.from(imageUrls);
  const batchSize = 4;

  for (let i = 0; i < normalizedImageUrls.length; i += batchSize) {
    const batchUrls = normalizedImageUrls.slice(i, i + batchSize);
    await Promise.all(
      batchUrls.map((imageUrl) =>
        getBlobUrlForImage(imageUrl, apiOrigin ?? getSurveyApiBaseUrl()),
      ),
    );
  }
};

function replaceSecureImagesWithBlobUrls<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((item) => replaceSecureImagesWithBlobUrls(item)) as T;
  }
  if (!value || typeof value !== "object") return value;

  const updatedEntries = Object.entries(value as Record<string, unknown>).map(
    ([key, val]) => {
      if (key === "image" && isRemoteImageUrl(val)) {
        return [key, imageBlobUrlCache.get(val) ?? val] as const;
      }
      return [key, replaceSecureImagesWithBlobUrls(val)] as const;
    },
  );
  return Object.fromEntries(updatedEntries) as T;
}

export const prepareSurveyQuestionImages = async <T>(
  data: T,
  apiOrigin?: URL,
): Promise<T> => {
  try {
    await prefetchRemoteImagesInBatches(data, apiOrigin);
  } catch {
    // Preserve the source URL when optional image prefetch fails.
  }
  return replaceSecureImagesWithBlobUrls(data);
};
